import AsyncStorage from "@react-native-async-storage/async-storage";
import { DEFAULT_ACTION_IDENTIFIER } from "./notificationResponses.js";
import {
  addAppMessage,
} from "../storage/appMessagesStorage.js";
import {
  applyFailPendingToMarkedDates,
  applySuccessToMarkedDates,
  getTodayDateKey,
  loadMarkedDatesRaw,
  saveMarkedDatesRaw,
} from "./markedDatesCheckIn.js";

const PROCESSED_RESPONSES_KEY = "notification_processed_responses_v1";

export const NOTIFICATION_ACTION_SUCCESS = "MARK_SUCCESS";
export const NOTIFICATION_ACTION_FAIL = "MARK_FAIL";

/**
 * @typedef {Object} NotificationResponse
 * @property {{ request: { identifier: string }; date: number }} notification
 * @property {string} actionIdentifier
 */

/**
 * @param {NotificationResponse} response
 */
function responseFingerprint(response) {
  const req = response?.notification?.request;
  const id = req?.identifier || "unknown";
  const action = response?.actionIdentifier || "";
  const date = response?.notification?.date || 0;
  return `${id}::${action}::${date}`;
}

/**
 * @param {string} fp
 */
async function wasResponseProcessed(fp) {
  try {
    const raw = await AsyncStorage.getItem(PROCESSED_RESPONSES_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) && list.includes(fp);
  } catch {
    return false;
  }
}

/**
 * @param {string} fp
 */
async function markResponseProcessed(fp) {
  try {
    const raw = await AsyncStorage.getItem(PROCESSED_RESPONSES_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const next = [fp, ...(Array.isArray(list) ? list : [])].slice(0, 40);
    await AsyncStorage.setItem(PROCESSED_RESPONSES_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

/**
 * @param {string} action
 * @param {string} [dateKey]
 */
export async function processCheckInNotificationAction(action, dateKey = getTodayDateKey()) {
  if (action === NOTIFICATION_ACTION_SUCCESS || action === "SUCCESS") {
    const dates = await loadMarkedDatesRaw();
    const updated = applySuccessToMarkedDates(dates, dateKey);
    await saveMarkedDatesRaw(updated);
    await addAppMessage({
      type: "success_notification",
      dateKey,
      title: "Marked success",
      body: "You tapped Mark today as success on the notification. Nice work — logged for today.",
      needsFailDetails: false,
    });
    return { kind: "success", dateKey };
  }

  if (action === NOTIFICATION_ACTION_FAIL || action === "RELAPSE" || action === "FAIL") {
    const dates = await loadMarkedDatesRaw();
    const updated = applyFailPendingToMarkedDates(dates, dateKey);
    await saveMarkedDatesRaw(updated);
    await addAppMessage({
      type: "fail_notification",
      dateKey,
      title: "Mark fail",
      body: "Today you tapped Mark fail on the notification. When you're ready, open Notifications to tell us what happened.",
      needsFailDetails: true,
      failDetailsCompleted: false,
    });
    return { kind: "fail", dateKey };
  }

  return { kind: "ignored" };
}

/**
 * @param {NotificationResponse|null|undefined} response
 */
export async function handleNotificationResponseIfNeeded(response) {
  if (!response) return null;
  const action = response.actionIdentifier;
  if (!action || action === DEFAULT_ACTION_IDENTIFIER) return null;

  const fp = responseFingerprint(response);
  if (await wasResponseProcessed(fp)) return null;
  await markResponseProcessed(fp);

  const result = await processCheckInNotificationAction(action);
  return result;
}
